import { Share2, Trash2 } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useCallback, useState } from "react";
import { Platform, Share } from "react-native";
import { type SheetProps, XStack, YStack } from "tamagui";
import { useStores } from "../../core/stores";
import { trackEvent } from "../../core/trackEvent";
import { PlanModelStep } from "../../plans/PlanModel";
import { Block } from "../Block";
import { Body } from "../Body";
import { Button } from "../Button";
import { Group } from "../Group";
import { Input } from "../InputNew";
import { PopoverConfirm } from "../PopoverConfirm";
import { Sheet } from "../Sheet";
import { Subtext } from "../Subtext";
import { Title } from "../Title";
import { UserAvatar } from "../UserAvatar";

export const ShareSheet: React.FC<SheetProps & { planUid: string }> = observer(
  ({ planUid, ...props }) => {
    const toast = useToastController();

    const { plansStore } = useStores();
    const plan = plansStore.byId.get(planUid);
    const [newUser, setNewUser] = useState("");
    const [_error, setError] = useState<string | undefined>();

    const _addUser = useCallback(
      (text?: string, isGhost?: boolean) => {
        if (!newUser && !text) {
          return;
        }

        plan
          ?.addUserToPlan(
            {
              avatarUrl: null,
              planUid: plan.uid,
              businessPlanUid: "",
              moduleVisits: {},
              lastActivity: serverTimestamp() as Timestamp,
              authedUsers: [],
              displayName: (text || newUser).trim(),
              email: null,
              tokens: [],
              profile: null,
            },
            isGhost,
          )
          .catch((e) => {
            toast.show(e.message, { duration: 2500 });
            setError(e.message);
          });

        trackEvent("share_create_user");

        setNewUser("");
      },
      [plan, toast, newUser],
    );

    const share = useCallback(() => {
      if (!plan) {
        return;
      }

      trackEvent("share_planira");

      Share.share({
        url: `https://planira.app/share/${plan.uid}`,
      }).catch(() => {
        if (Platform.OS === "web") {
          navigator.clipboard.writeText(
            `https://planira.app/share/${plan.uid}`,
          );

          toast.show("Share url copied to your clipboard", { duration: 2500 });
        } else {
          plan.setShowShare(false);
        }
      });

      plan.trackProgress(PlanModelStep.hasClickedOnShare, true);
    }, [toast, plan]);

    if (!plan) {
      return null;
    }

    return (
      <Sheet
        sheetId={plan.showShare.showMembers ? "members" : "share"}
        {...props}
        open={plan.showShare.show}
        onOpenChange={plan.setShowShare.bind(plan)}
        header={
          plan.showShare.showMembers ? (
            <Title>Members</Title>
          ) : (
            <Title>Invite</Title>
          )
        }
        footer={
          <YStack gap="$medium">
            <Body>
              Share the Planira URL with your group to get started planning
              together
            </Body>
            <Input
              multiline={false}
              preventNewLine
              disabled
              type="border"
              defaultValue={`https://planira.app/share/${plan.uid}`}
            />
            <Button icon={Share2} onPress={share}>
              Share Planira
            </Button>
          </YStack>
        }
      >
        {plan.showShare.showMembers && (
          <Group borderTop={false}>
            {plan.users.map((user) => {
              const canDelete =
                plan.isCreator || user.uid === plan.userInPlan?.uid;

              return (
                <Block
                  key={user.uid}
                  grouped
                  alignItems="center"
                  icon={<UserAvatar userUid={user.uid} />}
                >
                  <YStack gap="$small" width="100%">
                    <XStack width="100%" justifyContent="space-between">
                      <Body>{user.data.displayName}</Body>
                      {canDelete && (
                        <PopoverConfirm
                          onConfirm={() => plan.archiveUser(user.uid)}
                        >
                          <Trash2 size={"$size.normal"} color={"$muted"} />
                        </PopoverConfirm>
                      )}
                    </XStack>
                    {!user.isUsed && <Subtext>Not signed in yet</Subtext>}
                  </YStack>
                </Block>
              );
            })}
          </Group>
        )}
      </Sheet>
    );
  },
);

/*
<XStack gap="$medium" alignItems="center" width={"100%"}>
              <UserCircle name={newUser ? newUser : "??"} size={32} />

              <YStack flex={1} gap="$medium">
                <BodyTextarea
                  onChangeText={setNewUser}
                  type="border"
                  icon={UserPlus}
                  clearOnSubmit
                  submitOnEnter
                  defaultValue={newUser}
                  onSubmitEditing={(e) => addUser(e.nativeEvent.text)}
                  placeholder="Add new user by name..."
                />
              </YStack>
            </XStack>
 */
